import { useCallback, useMemo } from 'react';
import { useLocalStorage } from 'react-use';

import { useTranslation } from '@lyearn/i18n';
import { PersonOutline } from '@lyearn/icons';
import { Stack, Switch, Typography } from '@lyearn/ui';
import { UseOptionsQueryParams, UseOptionsQueryReturnType } from '@/components/FilterNew/types';
import { UsersFilters, UserStatusEnum } from '@/types/schema';

import { getUserFilterOption } from '../../utils';
import useUsersFilter from '../useUsersFilter';

export type UserQueryParams = Pick<
  UsersFilters,
  'ids' | 'userTypes' | 'parentId' | 'hierarchyPath' | 'OR'
> & { showMe?: boolean; deactivatedToggleId?: string; dependentFilters?: Partial<UsersFilters> };

export const USER_ME_FILTER = { property: 'ly__userId_me', label: 'Me' };

export const ME_OPTION = {
  value: USER_ME_FILTER.property,
  label: USER_ME_FILTER.label,
  name: USER_ME_FILTER.label,
  Icon: PersonOutline,
};

const useUserOptionsQuery = ({
  userTypes,
  ids,
  showMe,
  parentId,
  hierarchyPath,
  searchText,
  deactivatedToggleId,
  OR,
  dependentFilters,
}: UseOptionsQueryParams & UserQueryParams): UseOptionsQueryReturnType => {
  const { t } = useTranslation();
  const [checked, setChecked] = useLocalStorage<boolean>(
    deactivatedToggleId ?? 'deactivatedFilterToggle',
    false,
  );

  const {
    isLoading: fetching,
    users,
    fetchMore,
    error,
  } = useUsersFilter({
    filters: {
      searchText,
      userTypes,
      ids,
      parentId,
      hierarchyPath,
      OR,
      status: checked ? [UserStatusEnum.Active, UserStatusEnum.Archived] : undefined,
      ...dependentFilters,
    },
  });

  const groups = useMemo(() => {
    if (!users?.length) {
      return [];
    }

    const adaptedUsers = users.map((user) => getUserFilterOption(user));

    return [
      {
        id: 'users',
        options: showMe && !searchText ? [ME_OPTION, ...adaptedUsers] : adaptedUsers,
      },
    ];
  }, [users, showMe, searchText]);

  const renderFooter = useCallback(() => {
    return (
      <Stack
        alignItems="center"
        className="flex-shrink-0 px-10 py-12 border-t border-interactive-border"
        justifyContent="between">
        <Typography className="text-text-secondary" variant="body-short-02">
          {t('Admin:ShowDeactivatedLearners')}
        </Typography>
        <Switch checked={checked} onClick={() => setChecked(!checked)} />
      </Stack>
    );
  }, [checked, setChecked, t]);

  return {
    error,
    groups,
    fetching,
    fetchMore,
    renderFooter: deactivatedToggleId ? renderFooter : undefined,
  };
};

export default useUserOptionsQuery;
